import { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";

const GET_CAMPAIGN = gql`
  query MissionAdminCampaignForDetail($campaignId: Int!) {
    missionAdminCampaign(campaignId: $campaignId) {
      id
      brand {
        name
      }
      channel
      productName
      category
      originalPrice
      discountPrice
      paybackPrice
      productImageUrl
      productUrl
      isPublished
      photoReviewRequired
      steps {
        id
        stepOrder
        type
        title
        guide
        requirements
        notices
        proofType
        isRequired
        meta
        imageUrl
      }
      runs {
        id
        startAt
        endAt
        depositAmount
        prizeAmount
        isActive
        participationCount
        maxParticipants
        currentParticipants
        isFull
        remainingSlots
        note
      }
      createdAt
      updatedAt
    }
  }
`;

const DEACTIVATE_RUN = gql`
  mutation MissionAdminDeactivateRun($runId: Int!) {
    missionAdminDeactivateRun(runId: $runId) {
      ok
      error
    }
  }
`;

const PUBLISH_CAMPAIGN = gql`
  mutation MissionAdminPublishCampaign($campaignId: Int!) {
    missionAdminPublishCampaign(campaignId: $campaignId) {
      ok
      error
    }
  }
`;

const DELETE_CAMPAIGN = gql`
  mutation MissionAdminDeleteCampaign($campaignId: Int!) {
    missionAdminDeleteCampaign(campaignId: $campaignId) {
      ok
      error
    }
  }
`;

const CREATE_RUN = gql`
  mutation MissionAdminCreateRunForDetail(
    $campaignId: Int!
    $input: MissionAdminCreateRunInput!
  ) {
    missionAdminCreateRun(campaignId: $campaignId, input: $input) {
      id
      startAt
      endAt
      depositAmount
      prizeAmount
      maxParticipants
      currentParticipants
      isFull
      remainingSlots
    }
  }
`;

const SET_MISSION_STEPS = gql`
  mutation MissionAdminSetMissionSteps(
    $campaignId: Int!
    $steps: [MissionAdminMissionStepInput!]!
  ) {
    missionAdminSetMissionSteps(campaignId: $campaignId, steps: $steps) {
      id
      stepOrder
      type
      title
    }
  }
`;

const MissionAdminCampaignsDetail = () => {
  const { campaignId: campaignIdParam } = useParams<{ campaignId: string }>();
  const campaignId = campaignIdParam ? parseInt(campaignIdParam) : 0;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    "basic" | "missions" | "runs" | "preview"
  >("basic");
  const [isCreatingRun, setIsCreatingRun] = useState(false);
  const [editingGuideStepId, setEditingGuideStepId] = useState<number | null>(
    null
  );
  const [editedSteps, setEditedSteps] = useState<any[]>([]);
  const [runInfo, setRunInfo] = useState({
    runType: "separate" as "separate" | "continuous" | "timeSelect",
    selectedDates: [] as string[],
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    depositAmount: "",
    note: "",
    maxParticipantsType: "unlimited" as "unlimited" | "limited",
    maxParticipantsValue: "",
  });

  const { data, loading, error, refetch } = useQuery(GET_CAMPAIGN, {
    variables: { campaignId },
    skip: !campaignId,
  });

  const [deactivateRun] = useMutation(DEACTIVATE_RUN, {
    onCompleted: () => {
      refetch();
    },
  });

  const [publishCampaign] = useMutation(PUBLISH_CAMPAIGN, {
    onCompleted: () => {
      refetch();
    },
  });

  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, {
    onCompleted: () => {
      navigate("/ops/mission/campaigns");
    },
  });

  const [createRun, { loading: creatingRun }] = useMutation(CREATE_RUN, {
    onCompleted: () => {
      refetch();
      setIsCreatingRun(false);
    },
  });

  const [setSteps, { loading: settingSteps }] = useMutation(SET_MISSION_STEPS, {
    onCompleted: () => {
      refetch();
      setEditingGuideStepId(null);
      setEditedSteps([]);
      alert("가이드가 수정되었습니다.");
    },
    onError: (error) => {
      alert(`에러: ${error.message}`);
    },
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("ko-KR").format(price);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getChannelLabel = (channel: string) => {
    const labels: Record<string, string> = {
      NAVER: "네이버",
      COUPANG: "쿠팡",
      CARROT: "당근",
    };
    return labels[channel] || channel;
  };

  const handleDeactivateRun = async (runId: number) => {
    if (!window.confirm("정말 이 회차를 비활성화하시겠습니까?")) return;

    try {
      await deactivateRun({
        variables: { runId },
      });
      alert("회차가 비활성화되었습니다.");
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  const handleCreateRun = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!campaignId || campaignId === 0) {
      alert("캠페인 ID가 유효하지 않습니다.");
      return;
    }

    try {
      // 캠페인의 페이백 금액 사용
      const paybackPrice = campaign.paybackPrice;

      // maxParticipants 처리
      const maxParticipants =
        runInfo.maxParticipantsType === "limited" &&
        runInfo.maxParticipantsValue
          ? parseInt(runInfo.maxParticipantsValue)
          : null;

      if (runInfo.runType === "separate") {
        // 여러날 따로 생성: 선택한 날짜들 각각에 대해 회차 생성
        const validDates = runInfo.selectedDates.filter((date) => date !== "");
        if (validDates.length === 0) {
          alert("최소 하나 이상의 날짜를 선택해주세요.");
          return;
        }

        // 각 날짜에 대해 회차 생성
        for (const dateStr of validDates) {
          const selectedDate = new Date(dateStr);
          selectedDate.setHours(0, 0, 0, 0);
          const startAtISO = selectedDate.toISOString();

          const endDate = new Date(dateStr);
          endDate.setDate(endDate.getDate() + 1); // 다음날
          endDate.setHours(0, 0, 0, 0);
          const endAtISO = endDate.toISOString();

          await createRun({
            variables: {
              campaignId,
              input: {
                startAt: startAtISO,
                endAt: endAtISO,
                durationMinutes: null,
                depositAmount: parseInt(runInfo.depositAmount),
                prizeAmount: paybackPrice,
                note: runInfo.note || null,
                maxParticipants,
              },
            },
          });
        }
      } else if (runInfo.runType === "continuous") {
        // 여러날 이어서 생성: 하나의 회차 생성
        if (!runInfo.startDate || !runInfo.endDate) {
          alert("시작일과 종료일을 모두 입력해주세요.");
          return;
        }
        const startDate = new Date(runInfo.startDate);
        startDate.setHours(0, 0, 0, 0);
        const startAtISO = startDate.toISOString();

        const endDate = new Date(runInfo.endDate);
        endDate.setDate(endDate.getDate() + 1); // 다음날
        endDate.setHours(0, 0, 0, 0);
        const endAtISO = endDate.toISOString();

        await createRun({
          variables: {
            campaignId,
            input: {
              startAt: startAtISO,
              endAt: endAtISO,
              durationMinutes: null,
              depositAmount: parseInt(runInfo.depositAmount),
              prizeAmount: paybackPrice,
              note: runInfo.note || null,
              maxParticipants,
            },
          },
        });
      } else {
        // 커스터마이징: 날짜 + 시간 지정
        if (
          !runInfo.startDate ||
          !runInfo.endDate ||
          !runInfo.startTime ||
          !runInfo.endTime
        ) {
          alert("시작일, 종료일, 시작 시간, 종료 시간을 모두 입력해주세요.");
          return;
        }
        const startDate = new Date(
          `${runInfo.startDate}T${runInfo.startTime}:00`
        );
        const startAtISO = startDate.toISOString();

        const endDate = new Date(`${runInfo.endDate}T${runInfo.endTime}:00`);
        const endAtISO = endDate.toISOString();

        await createRun({
          variables: {
            campaignId,
            input: {
              startAt: startAtISO,
              endAt: endAtISO,
              durationMinutes: null,
              depositAmount: parseInt(runInfo.depositAmount),
              prizeAmount: paybackPrice,
              note: runInfo.note || null,
              maxParticipants,
            },
          },
        });
      }

      alert("회차가 생성되었습니다.");
      // 폼 초기화
      setRunInfo({
        runType: "separate",
        selectedDates: [],
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        depositAmount: "",
        note: "",
        maxParticipantsType: "unlimited",
        maxParticipantsValue: "",
      });
    } catch (error: any) {
      console.error("Run 생성 에러:", error);
      alert(`에러: ${error.message || JSON.stringify(error)}`);
    }
  };

  if (loading) return <div>로딩 중...</div>;
  if (error) return <div>에러: {error.message}</div>;
  if (!data?.missionAdminCampaign) return <div>캠페인을 찾을 수 없습니다.</div>;

  const campaign = data.missionAdminCampaign;
  const productUrlHref =
    campaign.productUrl && /^https?:\/\//i.test(campaign.productUrl)
      ? campaign.productUrl
      : campaign.productUrl
      ? `https://${campaign.productUrl}`
      : null;

  // 편집 가능 여부 계산: 모든 회차의 참여자가 0명일 때만 편집 가능
  const canEditGuide =
    campaign.runs.length === 0 ||
    campaign.runs.every((run: any) => run.participationCount === 0);

  // 편집 중인 스텝 데이터 관리
  const getStepData = (stepId: number) => {
    const edited = editedSteps.find((s) => s.id === stepId);
    if (edited) return edited;
    const original = campaign.steps.find((s: any) => s.id === stepId);
    return original;
  };

  const handleGuideEdit = (stepId: number) => {
    const step = campaign.steps.find((s: any) => s.id === stepId);
    if (step) {
      setEditingGuideStepId(stepId);
      setEditedSteps([
        ...editedSteps.filter((s) => s.id !== stepId),
        {
          ...step,
          title: step.title || "",
          guide: [...(step.guide || [])],
          requirements: [...(step.requirements || [])],
          notices: [...(step.notices || [])],
        },
      ]);
    }
  };

  const handleGuideCancel = () => {
    setEditingGuideStepId(null);
    setEditedSteps([]);
  };

  const handleGuideSave = async () => {
    if (!editingGuideStepId) return;

    try {
      // 현재 편집 중인 스텝의 데이터 가져오기
      const editedStep = editedSteps.find((s) => s.id === editingGuideStepId);
      if (!editedStep) return;

      // 모든 스텝을 업데이트 (편집 중인 스텝만 변경, 나머지는 기존 값 유지)
      const updatedSteps = campaign.steps.map((step: any) => {
        if (step.id === editingGuideStepId) {
          return {
            stepOrder: step.stepOrder,
            type: step.type,
            title: editedStep.title || null,
            guide: editedStep.guide || [],
            requirements: editedStep.requirements || [],
            notices: editedStep.notices || [],
            proofType: step.proofType,
            isRequired: step.isRequired ?? true,
            meta: step.meta || null,
            imageUrl: step.imageUrl || null,
          };
        }
        return {
          stepOrder: step.stepOrder,
          type: step.type,
          title: step.title || null,
          guide: step.guide || [],
          requirements: step.requirements || [],
          notices: step.notices || [],
          proofType: step.proofType,
          isRequired: step.isRequired ?? true,
          meta: step.meta || null,
          imageUrl: step.imageUrl || null,
        };
      });

      await setSteps({
        variables: {
          campaignId,
          steps: updatedSteps,
        },
      });
    } catch (error: any) {
      console.error("저장 에러:", error);
    }
  };

  const handleGuideChange = (
    stepId: number,
    guideIdx: number,
    value: string
  ) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        const newGuide = [...(existing.guide || [])];
        newGuide[guideIdx] = value;
        return prev.map((s) =>
          s.id === stepId ? { ...s, guide: newGuide } : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        const newGuide = [...(original.guide || [])];
        newGuide[guideIdx] = value;
        return [...prev, { ...original, guide: newGuide }];
      }
      return prev;
    });
  };

  const handleGuideAdd = (stepId: number) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        return prev.map((s) =>
          s.id === stepId ? { ...s, guide: [...(s.guide || []), ""] } : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        return [
          ...prev,
          { ...original, guide: [...(original.guide || []), ""] },
        ];
      }
      return prev;
    });
  };

  const handleGuideRemove = (stepId: number, guideIdx: number) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        const newGuide = [...(existing.guide || [])];
        newGuide.splice(guideIdx, 1);
        return prev.map((s) =>
          s.id === stepId ? { ...s, guide: newGuide } : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        const newGuide = [...(original.guide || [])];
        newGuide.splice(guideIdx, 1);
        return [...prev, { ...original, guide: newGuide }];
      }
      return prev;
    });
  };

  const handleTitleChange = (stepId: number, value: string) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        return prev.map((s) => (s.id === stepId ? { ...s, title: value } : s));
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        return [...prev, { ...original, title: value }];
      }
      return prev;
    });
  };

  const handleRequirementsChange = (
    stepId: number,
    reqIdx: number,
    value: string
  ) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        const newRequirements = [...(existing.requirements || [])];
        newRequirements[reqIdx] = value;
        return prev.map((s) =>
          s.id === stepId ? { ...s, requirements: newRequirements } : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        const newRequirements = [...(original.requirements || [])];
        newRequirements[reqIdx] = value;
        return [...prev, { ...original, requirements: newRequirements }];
      }
      return prev;
    });
  };

  const handleRequirementsAdd = (stepId: number) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        return prev.map((s) =>
          s.id === stepId
            ? { ...s, requirements: [...(s.requirements || []), ""] }
            : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        return [
          ...prev,
          { ...original, requirements: [...(original.requirements || []), ""] },
        ];
      }
      return prev;
    });
  };

  const handleRequirementsRemove = (stepId: number, reqIdx: number) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        const newRequirements = [...(existing.requirements || [])];
        newRequirements.splice(reqIdx, 1);
        return prev.map((s) =>
          s.id === stepId ? { ...s, requirements: newRequirements } : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        const newRequirements = [...(original.requirements || [])];
        newRequirements.splice(reqIdx, 1);
        return [...prev, { ...original, requirements: newRequirements }];
      }
      return prev;
    });
  };

  const handleNoticesChange = (
    stepId: number,
    noticeIdx: number,
    value: string
  ) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        const newNotices = [...(existing.notices || [])];
        newNotices[noticeIdx] = value;
        return prev.map((s) =>
          s.id === stepId ? { ...s, notices: newNotices } : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        const newNotices = [...(original.notices || [])];
        newNotices[noticeIdx] = value;
        return [...prev, { ...original, notices: newNotices }];
      }
      return prev;
    });
  };

  const handleNoticesAdd = (stepId: number) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        return prev.map((s) =>
          s.id === stepId ? { ...s, notices: [...(s.notices || []), ""] } : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        return [
          ...prev,
          { ...original, notices: [...(original.notices || []), ""] },
        ];
      }
      return prev;
    });
  };

  const handleNoticesRemove = (stepId: number, noticeIdx: number) => {
    setEditedSteps((prev) => {
      const existing = prev.find((s) => s.id === stepId);
      if (existing) {
        const newNotices = [...(existing.notices || [])];
        newNotices.splice(noticeIdx, 1);
        return prev.map((s) =>
          s.id === stepId ? { ...s, notices: newNotices } : s
        );
      }
      const original = campaign.steps.find((s: any) => s.id === stepId);
      if (original) {
        const newNotices = [...(original.notices || [])];
        newNotices.splice(noticeIdx, 1);
        return [...prev, { ...original, notices: newNotices }];
      }
      return prev;
    });
  };

  const handlePublish = async () => {
    if (
      !window.confirm(
        "정말 이 캠페인을 공개하시겠습니까?\n공개 후에는 삭제할 수 없습니다."
      )
    ) {
      return;
    }

    try {
      const { data } = await publishCampaign({
        variables: { campaignId },
      });

      if (data.missionAdminPublishCampaign.ok) {
        alert("캠페인이 공개되었습니다.");
      } else {
        alert(`공개 실패: ${data.missionAdminPublishCampaign.error}`);
      }
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "정말 이 캠페인을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다."
      )
    ) {
      return;
    }

    try {
      const { data } = await deleteCampaign({
        variables: { campaignId },
      });

      if (data.missionAdminDeleteCampaign.ok) {
        alert("캠페인이 삭제되었습니다.");
      } else {
        alert(`삭제 실패: ${data.missionAdminDeleteCampaign.error}`);
      }
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">캠페인 상세</h1>
        <div className="flex gap-2">
          {!campaign.isPublished && (
            <>
              <button
                onClick={() =>
                  navigate(`/ops/mission/campaigns/${campaignId}/edit`)
                }
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                편집
              </button>
              <button
                onClick={handlePublish}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                공개
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                삭제
              </button>
            </>
          )}
          {productUrlHref ? (
            <a
              href={productUrlHref}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 bg-white text-gray-800 rounded border border-gray-300 hover:bg-gray-50"
            >
              제품 링크
            </a>
          ) : null}
          <button
            onClick={() => navigate("/ops/mission/campaigns")}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            목록으로
          </button>
        </div>
      </div>

      {/* 탭 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {[
            { id: "basic", label: "기본 정보" },
            { id: "missions", label: "미션 단계" },
            { id: "runs", label: "회차 관리" },
            { id: "preview", label: "노출 상태 / 미리보기" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 기본 정보 탭 */}
      {activeTab === "basic" && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h2 className="text-xl font-semibold">기본 정보</h2>
            <span
              className={`shrink-0 px-2 py-1 rounded text-sm ${
                campaign.isPublished
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {campaign.isPublished ? "공개" : "비공개"}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 좌측: 텍스트 정보 */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    브랜드
                  </label>
                  <div className="mt-1 text-gray-900">
                    {campaign.brand.name}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    채널
                  </label>
                  <div className="mt-1 text-gray-900">
                    {getChannelLabel(campaign.channel)}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  제품명
                </label>
                <div className="mt-1 text-gray-900">{campaign.productName}</div>
              </div>

              {/* 가격 카드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  가격 정보
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="text-xs text-gray-500">정가</div>
                    <div className="mt-1 font-semibold text-gray-900">
                      {formatPrice(campaign.originalPrice)}원
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="text-xs text-gray-500">할인가</div>
                    <div className="mt-1 font-semibold text-gray-900">
                      {formatPrice(campaign.discountPrice)}원
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="text-xs text-gray-500">페이백</div>
                    <div className="mt-1 font-semibold text-gray-900">
                      {formatPrice(campaign.paybackPrice)}원
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 우측: 제품 이미지/링크 */}
            <div className="space-y-4">
              {campaign.productImageUrl ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    제품 이미지
                  </label>
                  <div className="w-full max-w-xs">
                    <div className="aspect-square w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                      <ResponsiveCDNImage
                        src={campaign.productImageUrl}
                        alt={`${campaign.productName} 이미지`}
                        className="w-full h-full object-cover"
                        sizeRatio={0.5}
                        lazy={false}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-gray-200 rounded-lg p-4 text-sm text-gray-500">
                  제품 이미지가 없습니다.
                </div>
              )}

              {productUrlHref ? (
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    제품 링크
                  </label>
                  <div className="flex items-center gap-2">
                    <a
                      href={productUrlHref}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      제품 페이지 열기
                    </a>
                    <span className="text-xs text-gray-500 break-all">
                      {campaign.productUrl}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-gray-200 rounded-lg p-4 text-sm text-gray-500">
                  제품 링크가 없습니다.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 미션 단계 탭 */}
      {activeTab === "missions" && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">미션 단계</h2>
            {!canEditGuide && (
              <div className="text-sm text-gray-500 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                참여자가 있는 회차가 있어 가이드를 수정할 수 없습니다.
              </div>
            )}
          </div>
          {campaign.steps.length === 0 ? (
            <p className="text-gray-500">등록된 미션 단계가 없습니다.</p>
          ) : (
            <div className="space-y-4">
              {campaign.steps.map((step: any) => {
                const isEditing = editingGuideStepId === step.id;
                const stepData = getStepData(step.id);
                const displayStep = stepData || step;
                const guide = displayStep.guide || [];
                const requirements = displayStep.requirements || [];
                const notices = displayStep.notices || [];

                return (
                  <div
                    key={step.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        {isEditing ? (
                          <div className="space-y-4">
                            {/* 타이틀 편집 */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                타이틀
                              </label>
                              <input
                                type="text"
                                value={displayStep.title || ""}
                                onChange={(e) =>
                                  handleTitleChange(step.id, e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                placeholder="미션 스텝 타이틀을 입력하세요"
                              />
                            </div>

                            {/* 가이드 편집 */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                가이드 (단계별 안내)
                              </label>
                              <div className="space-y-2">
                                {guide.map((g: string, idx: number) => (
                                  <div key={idx} className="flex gap-2">
                                    <input
                                      type="text"
                                      value={g}
                                      onChange={(e) =>
                                        handleGuideChange(
                                          step.id,
                                          idx,
                                          e.target.value
                                        )
                                      }
                                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                      placeholder="가이드 항목 입력"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleGuideRemove(step.id, idx)
                                      }
                                      className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                                    >
                                      삭제
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => handleGuideAdd(step.id)}
                                  className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                  + 가이드 항목 추가
                                </button>
                              </div>
                            </div>

                            {/* 필수 요구사항 편집 */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                필수 요구사항
                              </label>
                              <div className="space-y-2">
                                {requirements.map(
                                  (req: string, idx: number) => (
                                    <div key={idx} className="flex gap-2">
                                      <input
                                        type="text"
                                        value={req}
                                        onChange={(e) =>
                                          handleRequirementsChange(
                                            step.id,
                                            idx,
                                            e.target.value
                                          )
                                        }
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        placeholder="필수 요구사항 입력"
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleRequirementsRemove(step.id, idx)
                                        }
                                        className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                                      >
                                        삭제
                                      </button>
                                    </div>
                                  )
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleRequirementsAdd(step.id)}
                                  className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                  + 요구사항 추가
                                </button>
                              </div>
                            </div>

                            {/* 주의사항 편집 */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                주의사항 (꼭 알아야 할 점)
                              </label>
                              <div className="space-y-2">
                                {notices.map((notice: string, idx: number) => (
                                  <div key={idx} className="flex gap-2">
                                    <input
                                      type="text"
                                      value={notice}
                                      onChange={(e) =>
                                        handleNoticesChange(
                                          step.id,
                                          idx,
                                          e.target.value
                                        )
                                      }
                                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                      placeholder="주의사항 입력"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleNoticesRemove(step.id, idx)
                                      }
                                      className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                                    >
                                      삭제
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => handleNoticesAdd(step.id)}
                                  className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                  + 주의사항 추가
                                </button>
                              </div>
                            </div>

                            <div className="flex gap-2 mt-4">
                              <button
                                type="button"
                                onClick={handleGuideSave}
                                disabled={settingSteps}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
                              >
                                {settingSteps ? "저장 중..." : "저장"}
                              </button>
                              <button
                                type="button"
                                onClick={handleGuideCancel}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm"
                              >
                                취소
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {guide.length > 0 && (
                              <div className="mt-2">
                                <div className="text-sm font-medium">
                                  가이드:
                                </div>
                                <ul className="list-disc list-inside text-sm text-gray-600">
                                  {guide.map((g: string, idx: number) => (
                                    <li key={idx}>{g}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {requirements.length > 0 && (
                              <div className="mt-2">
                                <div className="text-sm font-medium">
                                  필수 요구사항:
                                </div>
                                <ul className="list-disc list-inside text-sm text-gray-600">
                                  {requirements.map(
                                    (req: string, idx: number) => (
                                      <li key={idx}>{req}</li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}
                            {notices.length > 0 && (
                              <div className="mt-2">
                                <div className="text-sm font-medium">
                                  주의사항:
                                </div>
                                <ul className="list-disc list-inside text-sm text-gray-600">
                                  {notices.map(
                                    (notice: string, idx: number) => (
                                      <li key={idx}>{notice}</li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}
                            {canEditGuide && (
                              <button
                                type="button"
                                onClick={() => handleGuideEdit(step.id)}
                                className="mt-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50"
                              >
                                편집
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="text-sm">
                        <span
                          className={`px-2 py-1 rounded ${
                            step.isRequired
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {step.isRequired ? "필수" : "선택"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 회차 관리 탭 */}
      {activeTab === "runs" && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">회차 관리</h2>
            <button
              onClick={() => setIsCreatingRun(!isCreatingRun)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {isCreatingRun ? "취소" : "회차 추가"}
            </button>
          </div>

          {/* 기존 회차 목록 (회차 추가 폼이 열렸을 때만 표시) */}
          {isCreatingRun && campaign.runs.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3 text-gray-700">
                기존 회차 ({campaign.runs.length}개)
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                {campaign.runs.map((run: any) => {
                  const now = new Date();
                  const startAt = new Date(run.startAt);
                  const endAt = new Date(run.endAt);
                  let status = "예정";
                  if (now >= startAt && now <= endAt) status = "진행 중";
                  else if (now > endAt) status = "종료";
                  else if (startAt.getTime() - now.getTime() <= 60 * 60 * 1000)
                    status = "모집 마감";

                  return (
                    <div
                      key={run.id}
                      className="border border-gray-300 rounded-lg p-3 bg-white"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {formatDate(run.startAt)} ~ {formatDate(run.endAt)}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            예치금: {formatPrice(run.depositAmount)}원 | 상금:{" "}
                            {formatPrice(run.prizeAmount)}원
                          </div>
                          <div className="text-xs text-gray-600">
                            참여:{" "}
                            {run.maxParticipants !== null
                              ? `${run.currentParticipants || 0}명 / ${
                                  run.maxParticipants
                                }명`
                              : `${run.currentParticipants || 0}명`}
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            status === "진행 중"
                              ? "bg-green-100 text-green-800"
                              : status === "종료"
                              ? "bg-gray-100 text-gray-800"
                              : status === "모집 마감"
                              ? "bg-red-100 text-red-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 회차 생성 폼 */}
          {isCreatingRun && (
            <form
              onSubmit={handleCreateRun}
              className="border border-gray-200 rounded-lg p-4 mb-6 space-y-4"
            >
              <div className="space-y-4">
                {/* 회차 선택 방식 (네모 버튼) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    회차 선택 방식 *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setRunInfo({
                          ...runInfo,
                          runType: "separate",
                        });
                      }}
                      className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                        runInfo.runType === "separate"
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      여러날 따로 생성
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setRunInfo({
                          ...runInfo,
                          runType: "continuous",
                        });
                      }}
                      className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                        runInfo.runType === "continuous"
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      여러날 이어서 생성
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setRunInfo({
                          ...runInfo,
                          runType: "timeSelect",
                        });
                      }}
                      className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                        runInfo.runType === "timeSelect"
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      커스터마이징
                    </button>
                  </div>
                </div>

                {runInfo.runType === "separate" ? (
                  // 여러날 따로 생성: 여러 날짜 멀티 셀렉
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      날짜 선택 * (여러 날짜 선택 가능)
                    </label>
                    <div className="space-y-2">
                      {runInfo.selectedDates.filter((d) => d).length === 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const now = new Date();
                            const localNow = new Date(
                              now.getTime() -
                                now.getTimezoneOffset() * 60 * 1000
                            );
                            const today = localNow.toISOString().split("T")[0];

                            setRunInfo((prev) => {
                              if (prev.selectedDates.includes(today))
                                return prev;

                              const firstEmptyIdx =
                                prev.selectedDates.findIndex((d) => !d);
                              if (firstEmptyIdx >= 0) {
                                const newDates = [...prev.selectedDates];
                                newDates[firstEmptyIdx] = today;
                                return { ...prev, selectedDates: newDates };
                              }

                              return {
                                ...prev,
                                selectedDates: [...prev.selectedDates, today],
                              };
                            });
                          }}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          오늘
                        </button>
                      )}
                      {runInfo.selectedDates.map((date, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="date"
                            value={date}
                            onChange={(e) => {
                              const newDates = [...runInfo.selectedDates];
                              newDates[index] = e.target.value;
                              setRunInfo({
                                ...runInfo,
                                selectedDates: newDates,
                              });
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                          />
                          {date && (
                            <button
                              type="button"
                              onClick={() => {
                                // 현재 날짜에서 하루를 더한 날짜 계산
                                const currentDate = new Date(date);
                                currentDate.setDate(currentDate.getDate() + 1);
                                const nextDate = currentDate
                                  .toISOString()
                                  .split("T")[0];

                                const newDates = [...runInfo.selectedDates];
                                newDates.splice(index + 1, 0, nextDate);
                                setRunInfo({
                                  ...runInfo,
                                  selectedDates: newDates,
                                });
                              }}
                              className="px-3 py-2 text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50"
                            >
                              하루 추가
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const newDates = runInfo.selectedDates.filter(
                                (_, i) => i !== index
                              );
                              setRunInfo({
                                ...runInfo,
                                selectedDates: newDates,
                              });
                            }}
                            className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                          >
                            삭제
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setRunInfo({
                            ...runInfo,
                            selectedDates: [...runInfo.selectedDates, ""],
                          });
                        }}
                        className="w-full px-4 py-2 text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                      >
                        + 날짜 추가
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      선택한 각 날짜마다 별도의 회차가 생성됩니다 (00:00 ~
                      다음날 00:00)
                    </p>
                  </div>
                ) : runInfo.runType === "continuous" ? (
                  // 여러날 이어서 생성: 시작일과 종료일만 선택
                  <div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          시작일 *
                        </label>
                        <input
                          type="date"
                          required
                          value={runInfo.startDate}
                          onChange={(e) =>
                            setRunInfo({
                              ...runInfo,
                              startDate: e.target.value,
                            })
                          }
                          className="w-full px-3 py-3 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          종료일 (포함) *
                        </label>
                        <input
                          type="date"
                          required
                          value={runInfo.endDate}
                          onChange={(e) =>
                            setRunInfo({ ...runInfo, endDate: e.target.value })
                          }
                          className="w-full px-3 py-3 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      시작일부터 종료일까지 하나의 회차가 생성됩니다 (시작일
                      00:00 ~ 종료일 다음날 00:00)
                    </p>
                  </div>
                ) : (
                  // 커스터마이징: 날짜 + 시간 지정
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          시작일 *
                        </label>
                        <input
                          type="date"
                          required
                          value={runInfo.startDate}
                          onChange={(e) =>
                            setRunInfo({
                              ...runInfo,
                              startDate: e.target.value,
                            })
                          }
                          className="w-full px-3 py-3 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          시작 시간 *
                        </label>
                        <input
                          type="time"
                          required
                          step="1800"
                          value={runInfo.startTime}
                          onChange={(e) =>
                            setRunInfo({
                              ...runInfo,
                              startTime: e.target.value,
                            })
                          }
                          className="w-full px-3 py-3 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          종료일 *
                        </label>
                        <input
                          type="date"
                          required
                          value={runInfo.endDate}
                          onChange={(e) =>
                            setRunInfo({ ...runInfo, endDate: e.target.value })
                          }
                          className="w-full px-3 py-3 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          종료 시간 *
                        </label>
                        <input
                          type="time"
                          required
                          step="1800"
                          value={runInfo.endTime}
                          onChange={(e) =>
                            setRunInfo({ ...runInfo, endTime: e.target.value })
                          }
                          className="w-full px-3 py-3 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    예치금 (원) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={runInfo.depositAmount}
                    onChange={(e) =>
                      setRunInfo({
                        ...runInfo,
                        depositAmount: e.target.value,
                      })
                    }
                    className="w-full px-3 py-3 border border-gray-300 rounded-md"
                  />
                </div>

                {/* 페이백 금액 표시 */}
                <div className="p-4 bg-blue-50 rounded-md">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">상금(페이백):</span>{" "}
                    <span className="font-bold text-blue-600">
                      {formatPrice(campaign.paybackPrice)}원
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      (캠페인 설정값이 자동으로 적용됩니다)
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    참여 인원수 제한
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="maxParticipants-unlimited-detail"
                        name="maxParticipants-detail"
                        value="unlimited"
                        checked={runInfo.maxParticipantsType === "unlimited"}
                        onChange={(e) =>
                          setRunInfo({
                            ...runInfo,
                            maxParticipantsType: e.target.value as
                              | "unlimited"
                              | "limited",
                          })
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="maxParticipants-unlimited-detail"
                        className="ml-2 text-sm text-gray-700"
                      >
                        무제한
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="maxParticipants-limited-detail"
                        name="maxParticipants-detail"
                        value="limited"
                        checked={runInfo.maxParticipantsType === "limited"}
                        onChange={(e) =>
                          setRunInfo({
                            ...runInfo,
                            maxParticipantsType: e.target.value as
                              | "unlimited"
                              | "limited",
                          })
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="maxParticipants-limited-detail"
                        className="ml-2 text-sm text-gray-700"
                      >
                        제한 설정
                      </label>
                      {runInfo.maxParticipantsType === "limited" && (
                        <input
                          type="number"
                          min="1"
                          value={runInfo.maxParticipantsValue}
                          onChange={(e) =>
                            setRunInfo({
                              ...runInfo,
                              maxParticipantsValue: e.target.value,
                            })
                          }
                          className="ml-3 px-3 py-2 border border-gray-300 rounded-md w-32"
                          placeholder="최대 인원수"
                          required={runInfo.maxParticipantsType === "limited"}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    운영 메모
                  </label>
                  <textarea
                    value={runInfo.note}
                    onChange={(e) =>
                      setRunInfo({ ...runInfo, note: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreatingRun(false);
                      setRunInfo({
                        runType: "separate",
                        selectedDates: [],
                        startDate: "",
                        endDate: "",
                        startTime: "",
                        endTime: "",
                        depositAmount: "",
                        note: "",
                        maxParticipantsType: "unlimited",
                        maxParticipantsValue: "",
                      });
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={creatingRun}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {creatingRun ? "생성 중..." : "생성"}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* 기존 회차 상세 목록 (회차 추가 폼이 닫혔을 때만 표시) */}
          {!isCreatingRun && (
            <>
              {campaign.runs.length === 0 ? (
                <p className="text-gray-500">등록된 회차가 없습니다.</p>
              ) : (
                <div className="space-y-4">
                  {campaign.runs.map((run: any) => {
                    const now = new Date();
                    const startAt = new Date(run.startAt);
                    const endAt = new Date(run.endAt);
                    let status = "예정";
                    if (now >= startAt && now <= endAt) status = "진행 중";
                    else if (now > endAt) status = "종료";
                    else if (
                      startAt.getTime() - now.getTime() <=
                      60 * 60 * 1000
                    )
                      status = "모집 마감";

                    return (
                      <div
                        key={run.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-semibold">
                              {formatDate(run.startAt)} ~{" "}
                              {formatDate(run.endAt)}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              예치금: {formatPrice(run.depositAmount)}원 | 상금:{" "}
                              {formatPrice(run.prizeAmount)}원
                            </div>
                            <div className="text-sm text-gray-600">
                              참여 현황:{" "}
                              {run.maxParticipants !== null
                                ? `${run.currentParticipants || 0}명 / ${
                                    run.maxParticipants
                                  }명`
                                : `${run.currentParticipants || 0}명`}
                              {run.maxParticipants !== null && (
                                <>
                                  {run.isFull ? (
                                    <span className="ml-2 text-red-600 font-semibold">
                                      (마감)
                                    </span>
                                  ) : run.currentParticipants &&
                                    run.maxParticipants &&
                                    run.currentParticipants >=
                                      run.maxParticipants * 0.8 ? (
                                    <span className="ml-2 text-orange-600 font-semibold">
                                      (마감 임박)
                                    </span>
                                  ) : (
                                    <span className="ml-2 text-green-600">
                                      (참여 가능)
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            {run.note && (
                              <div className="text-sm text-gray-500 mt-1">
                                메모: {run.note}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 rounded text-sm ${
                                status === "진행 중"
                                  ? "bg-green-100 text-green-800"
                                  : status === "종료"
                                  ? "bg-gray-100 text-gray-800"
                                  : status === "모집 마감"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {status}
                            </span>
                            {run.isActive && run.participationCount === 0 && (
                              <button
                                onClick={() => handleDeactivateRun(run.id)}
                                className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                              >
                                비활성화
                              </button>
                            )}
                            {run.participationCount > 0 && (
                              <span className="text-xs text-gray-500">
                                (참여자 있음 - 수정 불가)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 노출 상태 / 미리보기 탭 */}
      {activeTab === "preview" && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">노출 상태 / 미리보기</h2>
          <div className="space-y-4">
            {productUrlHref ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제품 외부 링크
                </label>
                <div className="flex items-center gap-2">
                  <a
                    href={productUrlHref}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    제품 페이지로 이동
                  </a>
                  <span className="text-xs text-gray-500 break-all">
                    {campaign.productUrl}
                  </span>
                </div>
              </div>
            ) : null}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                공개 상태
              </label>
              <div>
                <span
                  className={`px-2 py-1 rounded ${
                    campaign.isPublished
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {campaign.isPublished ? "공개" : "비공개"}
                </span>
              </div>
              {campaign.isPublished && (
                <div className="mt-2">
                  <a
                    href={`/campaigns/${campaign.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm inline-block"
                  >
                    유저 캠페인 페이지로 이동
                  </a>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                홈 노출 여부
              </label>
              <div className="text-sm text-gray-600">
                {campaign.isPublished &&
                campaign.runs.some(
                  (r: any) =>
                    r.isActive &&
                    new Date(r.startAt) > new Date() &&
                    new Date(r.startAt).getTime() - new Date().getTime() >
                      60 * 60 * 1000
                )
                  ? "노출됨 (시작 1시간 전까지)"
                  : "노출 안 됨"}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                리스트 노출 여부
              </label>
              <div className="text-sm text-gray-600">
                {campaign.isPublished &&
                campaign.runs.some(
                  (r: any) =>
                    r.isActive &&
                    new Date(r.startAt) > new Date() &&
                    new Date(r.startAt).getTime() - new Date().getTime() >
                      60 * 60 * 1000
                )
                  ? "노출됨 (2주 이내 시작 예정)"
                  : "노출 안 됨"}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionAdminCampaignsDetail;
